import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClubLifeComposer from "../PatientPortal/ClubLifeComposer";
import ClientPostManageModals from "../PatientPortal/ClientPostManageModals";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useClientPostManage } from "../../../hooks/useClientPostManage";
import { useAdminRefresh } from "../../../hooks/useAdminRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import { mediaUrl, TRAINER_COLOR_PRESETS } from "../../../utils/clientHeroTheme";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { Avatar, Skeleton, useToast } from "../../ui";
import aa from "./adminApp.module.css";
import s from "../PatientPortal/clientApp.module.css";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import md from "../common/manageDesktop.module.css";

const DEFAULT_ACCOUNT_BASE = "/manage/app/profile/account";

function themeStyle(themeId) {
  const preset = TRAINER_COLOR_PRESETS.find((p) => p.id === themeId);
  if (!preset) return {};
  return {
    "--member-accent": preset.color,
    "--ca-accent": preset.color,
  };
}

export default function AdminOwnerMemberProfile({
  accountBase = DEFAULT_ACCOUNT_BASE,
}) {
  const toast = useToast();
  const isApp = useAdminHubUi();
  const isMobile = useIsMobile(768);
  const isDesktopManage = !isApp && !isMobile;
  const myId = useSelector((state) => state.auth.profile?.id);
  const authProfile = useSelector((state) => state.auth.profile);
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("wall");
  const [wallPosts, setWallPosts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const coverInputRef = useRef(null);
  const { heroStyle } = useClubPortalBranding("aa");

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(tab === "wall" ? setWallPosts : setAchievements, {
    commentsApi: "admin",
  });
  const postManage = useClientPostManage(
    tab === "wall" ? setWallPosts : setAchievements,
    { apiMode: "staff" },
  );

  const loadProfile = useCallback(async () => {
    try {
      const data = await clinicAPI.getStaffMemberProfile();
      setProfile(data.profile || null);
    } catch (err) {
      setProfile(null);
      toast.error(err.response?.data?.error || "Не удалось загрузить профиль");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      if (tab === "wall") {
        const data = await clinicAPI.getStaffMemberWall();
        setWallPosts(data.posts || []);
      } else {
        const data = await clinicAPI.getStaffMemberAchievements();
        setAchievements(data.posts || []);
      }
    } catch {
      if (tab === "wall") setWallPosts([]);
      else setAchievements([]);
    } finally {
      setPostsLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!profile) return;
    loadPosts();
  }, [profile, loadPosts]);

  useAdminRefresh(() => {
    loadProfile();
    loadPosts();
  });

  const posts = tab === "wall" ? wallPosts : achievements;
  const shellStyle = useMemo(
    () => themeStyle(profile?.profile_theme),
    [profile?.profile_theme],
  );

  const handleWallPublished = useCallback(() => {
    loadPosts();
    loadProfile();
  }, [loadPosts, loadProfile]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await clinicAPI.uploadStaffMemberCover(file);
      setProfile((prev) =>
        prev ? { ...prev, profile_cover_url: r.url } : prev,
      );
    } catch {
      toast.error("Не удалось загрузить обложку");
    }
    e.target.value = "";
  };

  if (loading) {
    return <Skeleton height={320} style={{ borderRadius: 0 }} />;
  }

  if (!profile) {
    return (
      <div className={s.socialEmpty}>
        <span>🔒</span>
        <p>Профиль недоступен</p>
      </div>
    );
  }

  const coverUrl = mediaUrl(profile.profile_cover_url);
  const avatarPhoto = mediaUrl(profile.photo_url || authProfile?.photo_url);

  const statsLine = (
    <p className={s.memberVkSubline}>
      <strong>{profile.wall_posts_count ?? 0}</strong> записей ·{" "}
      <strong>{profile.followers_count ?? 0}</strong> подписчиков ·{" "}
      <strong>{profile.following_count ?? 0}</strong> подписок
    </p>
  );

  const profileShell = (
    <>
      <header
        className={`${s.memberVkHeader} ${
          isDesktopManage ? md.desktopMemberVkHeader : ""
        }`.trim()}
      >
        <div className={s.memberCoverWrap}>
          <div className={s.memberCover}>
            <div
              className={s.memberCoverBg}
              style={
                coverUrl
                  ? { backgroundImage: `url(${coverUrl})` }
                  : heroStyle
              }
            />
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={uploadCover}
            />
            {isDesktopManage ? (
              <div className={md.desktopCoverToolbar}>
                <Link
                  to={accountBase}
                  className={`${s.memberSettingsBtn} ${md.desktopOwnerMemberSettingsBtn}`}
                  aria-label="Настройки"
                >
                  ⚙
                </Link>
                <button
                  type="button"
                  className={`${s.memberCoverEdit} ${md.desktopCoverUploadBtn}`}
                  onClick={() => coverInputRef.current?.click()}
                >
                  {coverUrl ? "Заменить обложку" : "Загрузить обложку"}
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className={s.memberCoverEdit}
                  onClick={() => coverInputRef.current?.click()}
                >
                  Обложка
                </button>
                <Link
                  to={accountBase}
                  className={s.memberSettingsBtn}
                  aria-label="Настройки"
                >
                  ⚙
                </Link>
              </>
            )}
          </div>
        </div>

        <div
          className={`${s.memberVkProfile} ${
            isDesktopManage ? md.desktopMemberVkProfile : ""
          }`.trim()}
        >
          <div className={s.memberVkTopRow}>
            <div className={s.memberVkAvatarWrap}>
              <Avatar
                name={personDisplayName(profile.full_name)}
                src={avatarPhoto}
                size="lg"
                className={s.memberVkAvatar}
              />
            </div>
            <div className={s.memberVkTitleBlock}>
              <h2 className={s.memberVkName}>
                {personDisplayName(profile.full_name) || "Участник"}
              </h2>
              {statsLine}
              {profile.profile_bio ? (
                <p className={s.memberVkBio}>{profile.profile_bio}</p>
              ) : null}
            </div>
            {isDesktopManage ? (
              <div className={`${s.memberVkHeadActions} ${md.desktopVkHeadActions}`}>
                <Link to={`${accountBase}/data`} className={s.memberBtnPrimary}>
                  Редактировать профиль
                </Link>
              </div>
            ) : null}
          </div>

          {!isDesktopManage ? (
            <div className={s.memberBtnRow}>
              <Link to={`${accountBase}/data`} className={s.memberBtnOutline}>
                Редактировать
              </Link>
              <Link to={accountBase} className={s.memberBtnOutline}>
                Настройки
              </Link>
            </div>
          ) : null}
        </div>
      </header>

      <div
        className={`${s.memberBody} ${
          isDesktopManage ? md.desktopMemberBody : ""
        }`.trim()}
      >
        <nav className={s.memberTabs} aria-label="Разделы профиля">
          <button
            type="button"
            className={tab === "wall" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("wall")}
          >
            Стена
          </button>
          <button
            type="button"
            className={tab === "achievements" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("achievements")}
          >
            Достижения
          </button>
        </nav>

        {tab === "wall" ? (
          <div className={s.memberComposerSlot}>
            <ClubLifeComposer
              compact
              embedded
              wallMode
              staffWallMode
              onPublished={handleWallPublished}
            />
          </div>
        ) : null}

        {postsLoading ? (
          <Skeleton
            height={160}
            style={{ borderRadius: 16, margin: "12px 0" }}
          />
        ) : posts.length === 0 ? (
          <div className={s.memberFeedEmpty}>
            <span>{tab === "wall" ? "📝" : "🏆"}</span>
            <p>
              {tab === "wall"
                ? "Пока нет записей. Напишите на стене или поделитесь в ленте клуба."
                : "Достижений пока нет — публикуйте в разделе «Клуб»."}
            </p>
          </div>
        ) : (
          <div className={s.memberPosts}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode="admin"
                currentUserId={myId}
                onOpenMenu={postManage.openMenu}
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`${s.memberProfilePage} ${aa.ownerMemberProfilePage} ${
        isDesktopManage ? md.desktopOwnerMemberPage : ""
      }`.trim()}
      style={shellStyle}
    >
      {profileShell}
      <ClientPostManageModals {...postManage} />
    </div>
  );
}
